import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useControl } from 'react-map-gl/mapbox'
import type { ControlPosition, IControl } from 'react-map-gl/mapbox'
import { getImageUrl } from 'lib/image/imageUtils'
import type { MapStyleMode } from './map3DConfig'

const MAP_VIEW_OPTION_IMAGE_IDS = {
  standard: '595e97ee-0807-4871-828f-ba7bb055e82d',
  satellite: '3cb07d78-8dbe-41b8-b8ab-962f8040cbd3',
} as const

type ControlLabels = {
  switchToSatellite: string
  switchToStandard: string
}

function getNextStyleMode(mode: MapStyleMode): MapStyleMode {
  return mode === 'satellite' ? 'standard' : 'satellite'
}

class MapStyleToggleControlImpl implements IControl {
  private container: HTMLDivElement
  private button: HTMLButtonElement
  private previewImage: HTMLImageElement
  private mapStyleMode: MapStyleMode
  private onMapStyleModeChange?: (mode: MapStyleMode) => void
  private labels: ControlLabels

  constructor({
    mapStyleMode,
    onMapStyleModeChange,
    labels,
  }: {
    mapStyleMode: MapStyleMode
    onMapStyleModeChange: ((mode: MapStyleMode) => void) | undefined
    labels: ControlLabels
  }) {
    this.mapStyleMode = mapStyleMode
    this.onMapStyleModeChange = onMapStyleModeChange
    this.labels = labels
    this.container = document.createElement('div')
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'

    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.style.alignItems = 'center'
    this.button.style.display = 'flex'
    this.button.style.justifyContent = 'center'
    this.button.style.overflow = 'hidden'
    this.button.style.padding = '0'

    this.previewImage = document.createElement('img')
    this.previewImage.alt = ''
    this.previewImage.setAttribute('aria-hidden', 'true')
    this.previewImage.draggable = false
    this.previewImage.style.display = 'block'
    this.previewImage.style.height = '100%'
    this.previewImage.style.objectFit = 'cover'
    this.previewImage.style.width = '100%'

    this.button.append(this.previewImage)
    this.container.append(this.button)
    this.updateButton()
  }

  onAdd(): HTMLElement {
    this.button.addEventListener('click', this.handleClick)
    this.updateButton()
    return this.container
  }

  onRemove(): void {
    this.button.removeEventListener('click', this.handleClick)
    this.container.remove()
  }

  private handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const nextMode = getNextStyleMode(this.mapStyleMode)
    this.mapStyleMode = nextMode
    this.updateButton()
    this.onMapStyleModeChange?.(nextMode)
  }

  private updateButton() {
    const nextMode = getNextStyleMode(this.mapStyleMode)
    const ariaLabel = this.mapStyleMode === 'satellite'
      ? this.labels.switchToStandard
      : this.labels.switchToSatellite

    this.previewImage.src = getImageUrl(MAP_VIEW_OPTION_IMAGE_IDS[nextMode], {
      width: 29,
      height: 29,
      dpr: 2,
      quality: 'good',
    })
    this.button.setAttribute('aria-label', ariaLabel)
    this.button.title = ariaLabel
  }

  setLabels(labels: ControlLabels) {
    this.labels = labels
    this.updateButton()
  }

  setMode(mapStyleMode: MapStyleMode) {
    this.mapStyleMode = mapStyleMode
    this.updateButton()
  }
}

type Props = {
  position?: ControlPosition
  mapStyleMode: MapStyleMode
  onMapStyleModeChange?: (mode: MapStyleMode) => void
}

function MapStyleToggleControl({
  position = 'bottom-right',
  mapStyleMode,
  onMapStyleModeChange,
}: Props) {
  const intl = useIntl()
  const onMapStyleModeChangeRef = useRef(onMapStyleModeChange)
  const labels = useMemo(() => ({
    switchToSatellite: intl.formatMessage({
      id: 'QigsS66Wtoiuuvt50UPW',
      defaultMessage: 'Switch to satellite map',
      description: 'Accessible label for the Trip Planner map control that switches to satellite map imagery',
    }),
    switchToStandard: intl.formatMessage({
      id: 'SlQaDRdJ4MBJaZzxRKub',
      defaultMessage: 'Switch to standard map',
      description: 'Accessible label for the Trip Planner map control that switches from satellite imagery to the standard map',
    }),
  }), [intl])

  useEffect(() => {
    onMapStyleModeChangeRef.current = onMapStyleModeChange
  }, [onMapStyleModeChange])

  const control = useControl(
    () => new MapStyleToggleControlImpl({
      mapStyleMode,
      onMapStyleModeChange: (mode) => onMapStyleModeChangeRef.current?.(mode),
      labels,
    }),
    { position },
  )

  useEffect(() => {
    control.setLabels(labels)
  }, [control, labels])

  useEffect(() => {
    control.setMode(mapStyleMode)
  }, [control, mapStyleMode])

  return null
}

export default MapStyleToggleControl
